<template>
  <RevTabs
    class="mb-32 px-12"
    :label="i18n(label)"
    role="navigation"
    scrollable
    variant="flexible"
  >
    <RevTabItem
      v-for="tabItem in tabItems"
      :key="tabItem.label.id"
      :active="tabItem.to.name === activeTabItem.to.name"
      :class="withLabelClass(tabItem.to.name)"
      :disabled="tabItem.disabled"
      :label="i18n(tabItem.label)"
      role="tab"
      :to="tabItem.to"
    >
      <RevBadge
        v-if="withLabelSuffix && withLabelSuffix(tabItem.to.name) !== 0"
        :count="withLabelSuffix(tabItem.to.name)"
        variant="danger"
      />
    </RevTabItem>
  </RevTabs>

  <Suspense :key="activeTabItem.to.name">
    <template #fallback>
      <RevCard class="mt-32 px-56 py-32">
        <RevLoadingScreen v-if="true" :text="i18n(activeTabItem.loadingText)" />
      </RevCard>
    </template>
    <div class="mt-32">
      <TheAutonomousBanner page-level="TAB" />
      <NuxtPage />
    </div>
  </Suspense>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from '#imports'
import { computed } from 'vue'

import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevBadge } from '@ds/components/Badge'
import { RevCard } from '@ds/components/Card'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import translations from './NavTabs.translations'
import type { NavTabItem } from './NavTabs.types'

const props = withDefaults(
  defineProps<{
    label: I18nDefinition
    indexRouteName: string
    /**
     * If set to false the label won't be display.
     * this can be use to manage permissions
     */
    shouldShowLabel?: (routeName: string) => boolean

    /**
     * If set to true the label will be disable.
     * this can be use to manage permissions
     */
    shouldDisableLabel?: (routeName: string) => boolean

    /**
     * If a number is returned,
     * it will be added, in paranthesis after the label.
     * This is used as a count.
     */
    withLabelSuffix?: (routeName: string) => number

    /**
     * If provided, the class(es) will be applied to the RevTabItem
     */
    withLabelClass?: (routeName: string) => string
  }>(),
  {
    shouldShowLabel: () => true,
    shouldDisableLabel: () => false,
    withLabelSuffix: () => 0,
    withLabelClass: () => '',
  },
)

const i18n = useI18n()

const currentRoute = useRoute()
const router = useRouter()

// Maybe there is a better way to access to the children routes
// (currentRoute.matched doesn't work, as it only returns
// the index route + the current child route)
const routes =
  router.getRoutes().find((route) => route.name === props.indexRouteName)
    ?.children || []

const tabItems = routes.reduce((acc, route) => {
  const tabItem = route.meta?.tabItem
  const routeName = String(route.name)

  return !tabItem || !props.shouldShowLabel(routeName)
    ? acc
    : [
        ...acc,
        {
          to: { name: routeName },
          label: tabItem.label,
          disabled: props.shouldDisableLabel(routeName),
          loadingText: tabItem.loadingText || translations.loadingText,
        } as NavTabItem,
      ]
}, [] as NavTabItem[])

const activeTabItem = computed(
  () =>
    tabItems.find((tabItem) => {
      // We remove the potential .onboarding key from the route name
      const currentRouteName = currentRoute.name
        ?.toString()
        ?.replace(/^bo-seller\.onboarding\./, 'bo-seller.')

      // the includes is here in case of multiple nested nav tabs,
      // and check if the current route name is included in one of the parent tab item route name,
      // so that the parent nav tab is active when a nested one is active
      return currentRouteName?.includes(tabItem.to.name as string)
    }) || tabItems[0],
)
</script>
